import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Calendar, Tag, FileText, Loader2, CheckCircle2, ChevronRight, ChevronLeft, Building2, Plus } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { useToast } from '../../../src/contexts/ToastContext';
import { useAuth } from '../../../src/contexts/AuthContext';
import { propsFundoModal } from '../../../src/lib/backdropModal';

interface AgendarLembreteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

const gerarProximosMeses = (qtd = 12) => {
    const meses = [];
    const hoje = new Date();
    let mesAtual = hoje.getMonth();
    let anoAtual = hoje.getFullYear();

    for (let i = 0; i < qtd; i++) {
        meses.push(`${MESES[mesAtual]}/${anoAtual}`);
        mesAtual++;
        if (mesAtual > 11) {
            mesAtual = 0;
            anoAtual++;
        }
    }
    return meses;
};

const AgendarLembreteModal: React.FC<AgendarLembreteModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    // Step 1: Cliente/UC
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [searching, setSearching] = useState(false);
    const [selectedUcs, setSelectedUcs] = useState<any[]>([]);

    // Step 2: Quando
    const [dateMode, setDateMode] = useState<'mes' | 'periodo'>('mes');
    const [selectedMeses, setSelectedMeses] = useState<string[]>([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [mesesDisponiveis, setMesesDisponiveis] = useState<string[]>([]);

    // Step 3: Tipo
    const [tipoLembrete, setTipoLembrete] = useState<'acrescimo' | 'desconto' | 'outro'>('desconto');

    // Step 4: Descrição
    const [descricao, setDescricao] = useState('');

    useEffect(() => {
        setMesesDisponiveis(gerarProximosMeses(12));
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setStep(1);
            setSearchTerm('');
            setSearchResults([]);
            setSelectedUcs([]);
            setDateMode('mes');
            setSelectedMeses([]);
            setStartDate('');
            setEndDate('');
            setTipoLembrete('desconto');
            setDescricao('');
        }
    }, [isOpen]);

    useEffect(() => {
        const fetchUcs = async () => {
            if (searchTerm.length < 3) {
                setSearchResults([]);
                return;
            }
            setSearching(true);
            try {
                const { data, error } = await supabase
                    .from('unidades_consumidoras')
                    .select('id, numero_uc, outro_numero_uc, nome_cliente')
                    .or(`nome_cliente.ilike.%${searchTerm}%,numero_uc.ilike.%${searchTerm}%,outro_numero_uc.ilike.%${searchTerm}%`)
                    .limit(10);

                if (error) throw error;
                setSearchResults(data || []);
            } catch (err) {
                console.error(err);
            } finally {
                setSearching(false);
            }
        };

        const timeoutId = setTimeout(fetchUcs, 500);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const handleSave = async () => {
        if (selectedUcs.length === 0) return addToast('Selecione pelo menos uma UC.', 'error');
        if (dateMode === 'mes' && selectedMeses.length === 0) return addToast('Selecione pelo menos um mês de referência.', 'error');
        if (dateMode === 'periodo' && (!startDate || !endDate)) return addToast('Selecione o período.', 'error');
        if (!descricao.trim()) return addToast('Digite a descrição.', 'error');

        setLoading(true);
        try {
            const payload: any[] = [];
            const criadoPorNome = profile?.full_name || user?.user_metadata?.full_name || user?.email || 'Usuário Desconhecido';

            selectedUcs.forEach(uc => {
                if (dateMode === 'mes') {
                    selectedMeses.forEach(mes => {
                        payload.push({
                            uc_id: uc.id,
                            uc_codigo: uc.numero_uc || uc.outro_numero_uc,
                            nome_cliente: uc.nome_cliente,
                            tipo: tipoLembrete,
                            mes_referencia: mes,
                            data_inicio: null,
                            data_fim: null,
                            descricao: descricao,
                            status: 'pendente',
                            criado_por_id: user?.id || null,
                            criado_por_nome: criadoPorNome
                        });
                    });
                } else {
                    payload.push({
                        uc_id: uc.id,
                        uc_codigo: uc.numero_uc || uc.outro_numero_uc,
                        nome_cliente: uc.nome_cliente,
                        tipo: tipoLembrete,
                        mes_referencia: null,
                        data_inicio: startDate,
                        data_fim: endDate,
                        descricao: descricao,
                        status: 'pendente',
                        criado_por_id: user?.id || null,
                        criado_por_nome: criadoPorNome
                    });
                }
            });

            const { error } = await supabase.from('lembretes_faturamento').insert(payload);

            if (error) throw error;
            addToast(`${payload.length} lembrete(s) agendado(s) com sucesso!`, 'success');
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'lembretes_cobranca', acao: 'agendar_lembrete', origem: 'acao' });
            if (onSuccess) onSuccess();
            onClose();
        } catch (err: any) {
            console.error(err);
            addToast(err.message || 'Erro ao agendar lembretes.', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const renderStepIndicators = () => (
        <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors
                        ${step === i ? 'bg-primary text-white' :
                            step > i ? 'bg-primary/20 text-primary' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`}>
                        {step > i ? <CheckCircle2 size={16} /> : i}
                    </div>
                    {i < 4 && (
                        <div className={`w-12 h-1 rounded-full transition-colors ${step > i ? 'bg-primary/40' : 'bg-gray-100 dark:bg-gray-700'}`} />
                    )}
                </div>
            ))}
        </div>
    );

    return createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-scale-in flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between shrink-0">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                            <Calendar className="text-primary" />
                            Agendar Lembrete
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Crie um alerta para faturas futuras</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {renderStepIndicators()}

                    {/* STEP 1: Seleção de Cliente/UC */}
                    {step === 1 && (
                        <div className="space-y-4 animate-fade-in">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">Para quais clientes é o lembrete?</h3>

                            {/* UCs Selecionadas */}
                            {selectedUcs.length > 0 && (
                                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-1.5 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-800">
                                    {selectedUcs.map(uc => (
                                        <div key={uc.id} className="flex items-center gap-1.5 bg-primary/10 border border-primary/20 text-primary pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium animate-scale-in">
                                            <span className="truncate max-w-[150px] font-semibold">{uc.nome_cliente}</span>
                                            <span className="opacity-75 truncate max-w-[80px] font-normal">(UC: {uc.numero_uc || uc.outro_numero_uc})</span>
                                            <button 
                                                onClick={() => setSelectedUcs(prev => prev.filter(x => x.id !== uc.id))}
                                                className="p-0.5 hover:bg-primary/20 rounded-full transition-colors text-primary"
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="search"
                                    placeholder="Buscar por nome, UC..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all dark:text-white"
                                />
                                {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" size={18} />}
                            </div>

                            {searchResults.length > 0 ? (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-800 max-h-60 overflow-y-auto bg-white dark:bg-gray-800 shadow-sm">
                                    {searchResults.map(uc => {
                                        const isSelected = selectedUcs.some(x => x.id === uc.id);
                                        return (
                                            <button
                                                key={uc.id}
                                                onClick={() => {
                                                    if (isSelected) {
                                                        setSelectedUcs(prev => prev.filter(x => x.id !== uc.id));
                                                    } else {
                                                        setSelectedUcs(prev => [...prev, uc]);
                                                    }
                                                }}
                                                className={`w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex flex-col border-l-2 ${isSelected ? 'bg-primary/5 border-l-primary' : 'border-l-transparent'}`}
                                            >
                                                <div className="flex items-center justify-between w-full">
                                                    <span className="font-medium text-gray-800 dark:text-white truncate">{uc.nome_cliente}</span>
                                                    {isSelected && <CheckCircle2 size={16} className="text-primary shrink-0 ml-2" />}
                                                </div>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">UC: {uc.numero_uc || uc.outro_numero_uc}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : searchTerm.length >= 3 && !searching ? (
                                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">Nenhuma UC encontrada.</p>
                            ) : null}
                        </div>
                    )}

                    {/* STEP 2: Quando */}
                    {step === 2 && (
                        <div className="space-y-6 animate-fade-in">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">Para quando é o lembrete?</h3>

                            <div className="grid grid-cols-2 gap-3 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
                                <button
                                    onClick={() => setDateMode('mes')}
                                    className={`py-2 text-sm font-semibold rounded-lg transition-all ${dateMode === 'mes' ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                >
                                    Mês Específico
                                </button>
                                <button
                                    onClick={() => setDateMode('periodo')}
                                    className={`py-2 text-sm font-semibold rounded-lg transition-all ${dateMode === 'periodo' ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                >
                                    Período (Range)
                                </button>
                            </div>

                            {dateMode === 'mes' ? (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Selecione os Meses Referência</label>
                                        <span className="text-xs text-primary font-semibold bg-primary/10 px-2 py-0.5 rounded-full">{selectedMeses.length} selecionado(s)</span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        {mesesDisponiveis.map(mes => {
                                            const isSelected = selectedMeses.includes(mes);
                                            return (
                                                <button
                                                    key={mes}
                                                    onClick={() => {
                                                        if (isSelected) {
                                                            setSelectedMeses(prev => prev.filter(m => m !== mes));
                                                        } else {
                                                            setSelectedMeses(prev => [...prev, mes]);
                                                        }
                                                    }}
                                                    className={`py-2 px-3 border rounded-lg text-sm font-medium transition-all flex items-center justify-between ${isSelected ? 'border-primary bg-primary/10 text-primary font-bold ring-1 ring-primary/30' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                                >
                                                    <span>{mes}</span>
                                                    {isSelected && <CheckCircle2 size={12} className="text-primary" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Data de Início</label>
                                        <input
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 outline-none"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Data de Fim</label>
                                        <input
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 outline-none"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* STEP 3: Tipo */}
                    {step === 3 && (
                        <div className="space-y-4 animate-fade-in">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">Qual o tipo de agendamento?</h3>
                            <div className="grid grid-cols-1 gap-3">
                                {[
                                    { id: 'desconto', label: 'Desconto', icon: Tag, desc: 'Aplicar desconto na fatura' },
                                    { id: 'acrescimo', label: 'Acréscimo', icon: Plus, desc: 'Cobrar acréscimo na fatura' },
                                    { id: 'outro', label: 'Outro', icon: FileText, desc: 'Lembretes genéricos ou notas' }
                                ].map((t) => {
                                    const Icon = t.icon;
                                    return (
                                        <button
                                            key={t.id}
                                            onClick={() => setTipoLembrete(t.id as any)}
                                            className={`flex items-center gap-4 p-4 border rounded-xl transition-all text-left
                                                ${tipoLembrete === t.id
                                                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                                                }`}
                                        >
                                            <div className={`p-2 rounded-lg ${tipoLembrete === t.id ? 'bg-primary/20 text-primary' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                                                <Icon size={20} />
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-800 dark:text-white">{t.label}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">{t.desc}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* STEP 4: Descrição */}
                    {step === 4 && (
                        <div className="space-y-4 animate-fade-in">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">Descreva o lembrete</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Forneça os detalhes necessários para quem for analisar a fatura futuramente.</p>

                            <textarea
                                value={descricao}
                                onChange={(e) => setDescricao(e.target.value)}
                                rows={4}
                                placeholder="Ex: Adicionar R$ 50,00 de desconto ref. a indicação."
                                className="w-full p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-none"
                            />

                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700 space-y-2 mt-4">
                                <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Resumo do Lote</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm border-b border-gray-200 dark:border-gray-700 pb-2 mb-2">
                                    <div className="text-gray-500 flex items-center gap-1"><Building2 size={14} /> Total de UCs:</div>
                                    <div className="font-bold text-gray-900 dark:text-white text-right">{selectedUcs.length} UC(s)</div>

                                    <div className="text-gray-500 flex items-center gap-1"><Calendar size={14} /> Quando:</div>
                                    <div className="font-bold text-gray-900 dark:text-white text-right">
                                        {dateMode === 'mes' 
                                            ? `${selectedMeses.length} mês(es)` 
                                            : `${startDate.split('-').reverse().join('/')} até ${endDate.split('-').reverse().join('/')}`
                                        }
                                    </div>

                                    <div className="text-gray-500 flex items-center gap-1"><Tag size={14} /> Tipo:</div>
                                    <div className="font-medium text-gray-900 dark:text-white text-right capitalize">{tipoLembrete}</div>
                                </div>
                                <div className="flex justify-between items-center bg-primary/10 p-3 rounded-xl text-primary font-bold border border-primary/20 shadow-sm">
                                    <span className="text-xs flex items-center gap-1.5">
                                        <CheckCircle2 size={16} /> Lembretes a criar no total:
                                    </span>
                                    <span className="text-lg tracking-wider">
                                        {dateMode === 'mes' ? selectedUcs.length * selectedMeses.length : selectedUcs.length}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between shrink-0 bg-gray-50 dark:bg-gray-800/50">
                    {step > 1 ? (
                        <button
                            onClick={() => setStep(step - 1)}
                            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium flex items-center gap-1 transition-colors"
                        >
                            <ChevronLeft size={18} /> Voltar
                        </button>
                    ) : <div></div>}

                    {step < 4 ? (
                        <button
                            onClick={() => setStep(step + 1)}
                            disabled={
                                (step === 1 && selectedUcs.length === 0) ||
                                (step === 2 && dateMode === 'mes' && selectedMeses.length === 0) ||
                                (step === 2 && dateMode === 'periodo' && (!startDate || !endDate))
                            }
                            className="px-6 py-2 bg-primary hover:bg-primary-dark text-white font-bold rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Próximo <ChevronRight size={18} />
                        </button>
                    ) : (
                        <button
                            onClick={handleSave}
                            disabled={loading || !descricao.trim()}
                            className="px-6 py-2 bg-primary hover:bg-primary-dark text-white font-bold rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20"
                        >
                            {loading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                            Finalizar Agendamento
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AgendarLembreteModal;
